/**
 * Analytics service for tracking user interactions
 *
 * Note: This is a lightweight wrapper. In production, you would
 * typically integrate with PostHog, Google Analytics, or similar.
 */

import { logDebug, logError } from '@/utils/errorHandler';

interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
}

interface AnalyticsConfig {
  enabled: boolean;
  debug: boolean;
}

class AnalyticsService {
  private config: AnalyticsConfig = {
    enabled: true,
    debug: import.meta.env.DEV,
  };

  /**
   * Track a custom event
   */
  track(eventName: string, properties?: Record<string, any>) {
    if (!this.config.enabled) return;

    const event: AnalyticsEvent = {
      name: eventName,
      properties: {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...properties,
      },
    };

    if (this.config.debug) {
      logDebug('Analytics Event tracked', {
        context: 'AnalyticsService.track',
        eventName: eventName,
        event: event,
        category: 'analytics',
      });
    }

    // In production, send to your analytics service
    // this.sendToService(event)
  }

  /**
   * Track page view with section parameter
   */
  trackPageView(section?: string) {
    this.track('docs_page_view', {
      section: section || 'unknown',
      page: 'documentation',
    });
  }

  /**
   * Track feedback submission
   */
  trackFeedbackSubmitted(hasEmail: boolean) {
    this.track('feedback_submitted', {
      has_email: hasEmail,
      source: 'documentation',
    });
  }

  /**
   * Track section visibility (when heading crosses viewport)
   */
  trackSectionView(sectionId: string, sectionTitle: string) {
    this.track('docs_section_view', {
      section_id: sectionId,
      section_title: sectionTitle,
      viewport_percentage: 50, // Threshold for tracking
    });
  }

  /**
   * Configure analytics service
   */
  configure(config: Partial<AnalyticsConfig>) {
    this.config = { ...this.config, ...config };
  }

  /**
   * Send event to external service (placeholder)
   */
  private sendToService(event: AnalyticsEvent) {
    // Example: PostHog integration
    // if (window.posthog) {
    //   window.posthog.capture(event.name, event.properties)
    // }

    // Example: Google Analytics integration
    // if (window.gtag) {
    //   window.gtag('event', event.name, event.properties)
    // }

    // For now, just store in localStorage for debugging
    try {
      const events = JSON.parse(localStorage.getItem('analytics_events') || '[]');
      events.push(event);

      // Keep only last 100 events
      if (events.length > 100) {
        events.splice(0, events.length - 100);
      }

      localStorage.setItem('analytics_events', JSON.stringify(events));
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'AnalyticsService',
        action: 'sendToService',
        metadata: { eventName: event.name },
      });
    }
  }
}

// Create singleton instance
export const analytics = new AnalyticsService();

// Export types for TypeScript
export type { AnalyticsEvent, AnalyticsConfig };
